import type { ErrorEvent, EventHint } from '@sentry/nextjs';
import {
  getCurrentScope,
  getGlobalScope,
  getIsolationScope,
  init,
} from '@sentry/nextjs';

import {
  findIgnoredPattern,
  findRecoveredConsoleIgnoredPattern,
  isExternalScriptError,
} from './client-ignore-list';
import {
  isRscPrefetchFallbackConsoleCapture,
  recoverConsoleObjectMessage,
  RSC_PREFETCH_FALLBACK_DROP_VALUE,
  RSC_PREFETCH_FALLBACK_MESSAGE,
} from './console-capture-guards';
import type {
  DropReason,
  IgnoredDropHandler,
  IgnoredDropInfo,
} from './drop-telemetry';
import {
  buildDropInfo,
  DROP_REASONS,
  isLocalOrigin,
  normalizeRoutePath,
  resolveEventText,
} from './drop-telemetry';
import { isPreviewEnvironment } from './preview-environment';
import type {
  ProcessorDropHandler,
  ProcessorDropInfo,
  SinkArmedHandler,
  SinkArmedInfo,
} from './processor-drop-telemetry';
import { instrumentEventProcessorDrops } from './processor-drop-telemetry';
import {
  isMissingSupabaseKeysError,
  isPreviewSupabaseKeysTypeError,
} from './supabase-key-errors';
import {
  GOOGLE_PLACES_ID_CHANGED_DROP_VALUE,
  GOOGLE_PLACES_ID_CHANGED_NOTICE,
  isGooglePlacesIdChangedNotice,
  isPageHarnessAddEventListenerHook,
  isRrwebCssSerializerWarning,
  PAGE_HARNESS_PATTERN_SLUG,
  RRWEB_CSS_SERIALIZER_WARNING,
} from './third-party-noise-guards';

type Parameters<T extends (args: never) => unknown> = T extends (
  ...args: infer P
) => unknown
  ? P
  : never;

/**
 * Resolve the Sentry environment tag from Vercel-injected env vars first,
 * falling back to NODE_ENV for local / non-Vercel hosts.
 *
 * On the browser, only NEXT_PUBLIC_* env vars are available — we expose
 * VERCEL_ENV via NEXT_PUBLIC_VERCEL_ENV in next.config.mjs (or via the
 * Vercel system env-var mirror) so previews are tagged correctly.
 *
 * VERCEL_ENV values: 'production' | 'preview' | 'development'
 */
function resolveEnvironment(): string {
  return (
    process.env.NEXT_PUBLIC_VERCEL_ENV ??
    process.env.VERCEL_ENV ??
    process.env.NODE_ENV ??
    'unknown'
  );
}

// Re-exported: the console-capture tests and any caller of `./config/client`
// reach it through this module.
export { recoverConsoleObjectMessage };

/**
 * Telemetry hook for events discarded by IGNORED_ERROR_PATTERNS (BAL-856).
 * beforeSend returns null client-side, so without this hook a drop leaves
 * zero trace anywhere — a 496-event burst on 2026-07-29 and a 577-event
 * burst on 2026-08-09/10 were only visible as an aggregate discard counter.
 * The consumer (apps/web/instrumentation-client.ts) forwards drops to
 * PostHog so per-pattern per-day counts are queryable and alertable.
 *
 * Shape and sanitization live in `drop-telemetry.ts`: since BAL-891 the
 * browser, Node and Edge halves all write the SAME `sentry_event_dropped`
 * event, so they must redact identically or the table is safe on some rows
 * and raw on others.
 */
export type { IgnoredDropInfo, IgnoredDropHandler };

/**
 * BAL-959: the `event_processor` half of the same story. `onIgnoredDrop` above
 * covers only `beforeSend` drops, which Sentry counts under a DIFFERENT
 * `client_discard` reason — so five bursts counted as `event_processor`
 * (peaking at ~1700 events in the 2026-08-19T22:00Z hour) left no row in the
 * telemetry table and were closed as unattributable four times over.
 */
export type { ProcessorDropInfo, ProcessorDropHandler };
export type { SinkArmedInfo, SinkArmedHandler };

// Cap telemetry per drop key per pageload so a looping client can't turn a
// swallowed error burst into a PostHog event burst. 20 is enough to make a
// burst unmistakable across sessions while bounding a single pathological tab.
//
// The key is `reason|pattern`, not `pattern` alone (BAL-953). Keying on pattern
// would give all nine reason-only branches ONE shared 20-row budget under the
// empty pattern, so a preview-env burst would exhaust the budget for a
// localhost burst on the same pageload and hide it — reintroducing the blind
// spot one level down. Per-pattern behaviour for the ignore-list is unchanged:
// its key is `ignore-pattern|<regex source>`.
const MAX_DROP_REPORTS_PER_PATTERN = 20;

/**
 * @name initializeSentryBrowserClient
 * @description Initialize the Sentry client
 * @param props
 */
export function initializeSentryBrowserClient(
  props: Parameters<typeof init>[0] & {
    onIgnoredDrop?: IgnoredDropHandler;
    onProcessorDrop?: ProcessorDropHandler;
    onSinkArmed?: SinkArmedHandler;
  } = {},
) {
  // Extract beforeSend and integrations from props to prevent them from
  // overriding ours via spread. A caller's beforeSend is chained after our
  // filters; a caller's integrations are merged after replayIntegration.
  const {
    beforeSend: callerBeforeSend,
    integrations: callerIntegrations,
    onIgnoredDrop,
    onProcessorDrop,
    onSinkArmed,
    ...restProps
  } = props ?? {};

  const resolvedEnvironment = resolveEnvironment();

  // Per-pageload throttle state for onIgnoredDrop (BAL-856).
  const dropReportCounts = new Map<string, number>();

  // Re-entrancy guard. `onIgnoredDrop` runs a PostHog capture from inside
  // `beforeSend`; if that capture ever `console.error`s, `captureConsoleIntegration`
  // (registered with `levels: ['error', 'warn']`) turns it into a new Sentry
  // event that re-enters `beforeSend`. The per-key cap already terminates that
  // cycle at 20, so this is not load-bearing — but it stops the cycle outright
  // instead of budgeting for it, and it matches the guard the sibling sink in
  // `processor-drop-telemetry.ts` already carries.
  let reporting = false;

  /**
   * Attribute one `beforeSend` drop. Called on EVERY `return null` below —
   * that completeness is the whole point of BAL-953, because Sentry's
   * `client_discard / before_send` counter counts every one of them and a
   * telemetry table covering a subset can never reconcile against it.
   *
   * There is no pageload-wide ceiling here, unlike the processor sink, and that
   * is deliberate. Worst case is 20 rows × the number of live keys, and the
   * keys are a fixed set (one per ignore pattern, plus one per reason) rather
   * than the unbounded per-message signatures the processor sink faces. A total
   * ceiling would buy a ~20% volume reduction by silently truncating whichever
   * attribution arrived last — which is the failure this sink exists to remove.
   */
  function reportIgnoredDrop(args: {
    reason: DropReason;
    errorValue: string;
    pattern?: string;
  }) {
    if (!onIgnoredDrop) return;
    if (reporting) return;
    const { reason, errorValue, pattern = '' } = args;
    const key = `${reason}|${pattern}`;
    const count = dropReportCounts.get(key) ?? 0;
    if (count >= MAX_DROP_REPORTS_PER_PATTERN) return;
    dropReportCounts.set(key, count + 1);
    reporting = true;
    try {
      onIgnoredDrop(
        buildDropInfo({
          reason,
          pattern,
          errorValue,
          // BAL-1020: marks the last row this key may write this pageload, so
          // a capped burst reads as a floor instead of as complete.
          keyExhausted: count + 1 >= MAX_DROP_REPORTS_PER_PATTERN,
          // ONE place decides whether the text ships, and it keys on the
          // ORIGIN rather than on which branch fired. Keying on the reason
          // instead would have covered only the `localhost` branch — and that
          // branch tests two exact hostnames, so a dev machine on `0.0.0.0`,
          // `::1`, or a LAN address falls through to a later reason and would
          // have shipped its text. Found by the BAL-953 security review.
          // `undefined` when `window` is absent — this module is pulled into
          // the SERVER graph via the @kit/sentry barrel. That correctly reads
          // as production in a production build; under `next dev` the build
          // signal inside `isLocalOrigin` covers it.
          suppressMessage: isLocalOrigin(
            typeof window !== 'undefined'
              ? window.location.hostname
              : undefined,
          ),
          route:
            typeof window !== 'undefined'
              ? normalizeRoutePath(window.location.pathname)
              : 'unknown',
          environment: resolvedEnvironment,
        }),
      );
    } catch {
      // Telemetry must never break the drop path.
    } finally {
      reporting = false;
    }
  }

  /**
   * Discard the event, attributed. The ONLY way `beforeSend` returns null.
   *
   * Dropping and attributing the drop are one statement here, so they cannot
   * come apart — which is the actual BAL-953 fix. BAL-856 and BAL-891 each
   * added a `reportIgnoredDrop` call NEXT TO one `return null` and left the
   * rest untouched; nothing failed, because a silent drop
   * looks exactly like an event that never happened. Convention had two
   * chances and lost both. `drop-reason-coverage.test.ts` fails the build on a
   * bare `return null` inside `beforeSend`.
   */
  function dropEvent(args: {
    reason: DropReason;
    errorValue: string;
    pattern?: string;
  }): null {
    reportIgnoredDrop(args);
    return null;
  }

  const client = init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

    // Derive Sentry environment from VERCEL_ENV so preview deployments don't
    // pollute the production signal. Caller can still override via restProps.
    environment: resolvedEnvironment,

    // Use tunnel to bypass ad blockers that block requests to sentry.io
    // @see https://docs.sentry.io/platforms/javascript/troubleshooting/#using-the-tunnel-option
    tunnel: '/api/monitoring/sentry',

    // Replay may only be enabled for the client-side. Without
    // replayIntegration() registered here, replaysSessionSampleRate /
    // replaysOnErrorSampleRate below are dead config — Sentry records
    // nothing (0 replays org-wide while BALLEE-E3 sat unreproducible).
    // Merge caller integrations WITH SDK defaults instead of letting the
    // restProps spread replace them. NOTE: replayIntegration() cannot live
    // here — this module is also pulled into the server graph via the
    // @kit/sentry barrel, and @sentry/nextjs's node export has no
    // replayIntegration (Turbopack hard-fails the build). It is registered
    // in apps/web/instrumentation-client.ts, which is browser-graph-only.
    // Without it there, replaysSessionSampleRate / replaysOnErrorSampleRate
    // below are dead config and Sentry records no replays at all.
    integrations: (defaults) =>
      typeof callerIntegrations === 'function'
        ? callerIntegrations(defaults)
        : [...defaults, ...(callerIntegrations ?? [])],

    // BAL-973: the package DEFAULT, used only when a caller does not pass its
    // own tracesSampleRate. This used to default to 1.0 — the literal
    // starter-template rate that exhausted the org-wide span quota three
    // cycles running (BAL-896) — and was unguarded by any test: apps/web's
    // own `instrumentation-client.ts` always passes an explicit rate, and
    // `SentryMonitoringService`'s browser branch (packages/monitoring/sentry/
    // src/services/sentry-monitoring.service.ts) yields instead of
    // re-initializing whenever a client already exists (see
    // `browser-init-yields-to-instrumentation.test.ts`) — but that guard only
    // holds because `instrumentation-client.ts` happens to run first. Any
    // consumer that constructs the service before that hook, or any
    // non-Next.js caller of this package, gets this default directly.
    // Matches BASE_TRACES_SAMPLE_RATE in apps/web/lib/sentry-traces-sampler.ts
    // so that path lands on the current safe policy, not the old one.
    tracesSampleRate: restProps?.tracesSampleRate ?? 0.05,

    // Capture Replay ONLY for sessions with an error. Session sampling is 0
    // because the plan's replay quota (50 per billing period) goes to healthy
    // sessions at any nonzero rate: measured 2026-09-02, 48 of the 50 replays
    // accepted in the period carry `count_errors = 0`, after which Sentry
    // rejects the error replays too. A caller may override via restProps,
    // which is spread below this line, but only with the quota headroom to
    // pay for it.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Note: if you want to override the automatic release value, do not set a
    // `release` value here - use the environment variable `SENTRY_RELEASE`, so
    // that it will also get attached to your source maps,
    ...restProps,

    // AFTER the spread so a caller cannot turn it on. Both console-warning
    // guards (rrweb, Google Places) require an event with no exception, and
    // `attachStacktrace: true` attaches a synthetic one to every console
    // message, silently disarming them.
    attachStacktrace: false,

    // beforeSend AFTER the spread so it's never overridden
    beforeSend(event: ErrorEvent, hint: EventHint) {
      // Raw text for the drop branches that fire before any message matching.
      // Lazy on purpose, and it must stay lazy: the branches after the
      // console-message recovery below need the RECOVERED text, which is
      // written back onto `event.message` mid-function. Snapshotting up front
      // would report `[object Object]` on every drop after that point.
      // `buildDropInfo` redacts what this returns — nothing raw reaches the
      // sink.
      const dropText = () =>
        resolveEventText({
          message: event.message,
          originalError: hint.originalException,
          exceptionValue: event.exception?.values?.[0]?.value,
        });

      // Drop everything from Vercel preview deployments (BAL-791). Previews
      // point at the throwaway e2e Supabase branch and report its rebuild
      // artifacts (42501 permission-denied, BALLEE-GK/GJ) as if they were
      // product defects. Deliberate tradeoff: preview-only crashes no longer
      // reach Sentry. Production/staging are untouched.
      //
      // Reported (BAL-953). The `event_processor` sink deliberately does NOT
      // report preview/localhost drops — see `shouldReport` in
      // `processor-drop-telemetry.ts` — and that asymmetry is intentional
      // rather than an oversight. This half exists to reconcile against
      // Sentry's `before_send` discard counter, which counts these; excluding
      // them would guarantee the two never add up and would leave the
      // localhost-tagged-production path, a named burst candidate, silent.
      if (isPreviewEnvironment(resolvedEnvironment)) {
        return dropEvent({
          reason: DROP_REASONS.previewEnvironment,
          errorValue: dropText(),
        });
      }

      // Drop events from local dev. Misconfigured local environments stamp
      // `production` on the environment tag while serving from localhost:3012,
      // which floods the production project with noise.
      //
      // The message text does not ship from here — but that is decided in
      // `reportIgnoredDrop` by the ORIGIN, not by this branch, so a dev machine
      // that misses these two exact hostnames is covered too. See the
      // `suppressMessage` note there.
      //
      // This condition stays EXACTLY as it was: it decides what Sentry drops,
      // not what telemetry ships, and widening it would silence events that
      // currently reach Sentry.
      if (
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1')
      ) {
        return dropEvent({
          reason: DROP_REASONS.localhost,
          errorValue: dropText(),
        });
      }

      const originalError = hint.originalException;
      const eventExceptionValue =
        event.exception?.values?.[0]?.value?.toLowerCase() || '';

      // Recover the real text from structured-log console captures so the
      // issue title and Sentry's own inbound filters see it instead of
      // `[object Object]` (BALLEE-GZ). Our own filters below deliberately do
      // NOT all see it: the Cloudflare heuristic reads the ORIGINAL message,
      // and the ignore list is narrowed for recovered text (BAL-960/961).
      const originalMessage = event.message;
      const recoveredConsoleMessage = recoverConsoleObjectMessage(event);
      if (recoveredConsoleMessage) {
        event.message = recoveredConsoleMessage;
      }

      // Cloudflare-origin-timeout fingerprint. Mirror of the server-side
      // filter — when Supabase Postgres is unreachable, REST calls return
      // an HTML 5xx body from Cloudflare (522/523/524) that surfaces in
      // the browser as a fetch parse failure. Group all such events under
      // one fingerprint so a single outage doesn't produce hundreds of
      // unrelated-looking issues. See `sentry.server.config.ts` for the
      // canonical comment block.
      //
      // Reads the message as it ARRIVED, not the recovered text (BAL-961).
      // This branch stamps `outage_signal` and returns early past every other
      // filter. `[object Object]` could never match `/\b52[234]\b/`; a
      // recovered log line can ("Request failed with status code 524" against
      // an unrelated API), which would page for an outage that is not
      // happening. A real Cloudflare body arrives AS the message.
      const rawMsg = String(
        originalMessage ??
          (originalError instanceof Error
            ? originalError.message
            : typeof originalError === 'string'
              ? originalError
              : (event.exception?.values?.[0]?.value ?? '')),
      );
      if (
        rawMsg.includes('<!DOCTYPE html>') ||
        /\b52[234]\b/.test(rawMsg) ||
        rawMsg.startsWith('<html')
      ) {
        event.fingerprint = ['cloudflare-origin-timeout'];
        event.tags = {
          ...event.tags,
          upstream_class: 'cloudflare-522',
          outage_signal: 'true',
        };
        event.level = 'error';
        if (event.message && event.message.length > 200) {
          event.message =
            event.message.slice(0, 200) + '... [HTML body truncated]';
        }
        const exceptionValue = event.exception?.values?.[0]?.value;
        if (exceptionValue && exceptionValue.length > 200) {
          event.exception!.values![0]!.value =
            exceptionValue.slice(0, 200) + '... [HTML body truncated]';
        }
        return event;
      }

      // Drop "missing NEXT_PUBLIC_SUPABASE_*" ZodErrors from non-production
      // environments. Vercel preview deploys without these env vars crash
      // every page render — capturing one is fine, capturing 100s/hour
      // pollutes the prod signal. See BAL-169.
      if (
        resolvedEnvironment !== 'production' &&
        isMissingSupabaseKeysError(originalError, eventExceptionValue)
      ) {
        return dropEvent({
          reason: DROP_REASONS.missingSupabaseKeys,
          errorValue: dropText(),
        });
      }

      // Drop the getter TypeError ("Cannot set property message of
      // [object Object] which has only a getter") that the pre-9d29f582f4
      // catch block in getSupabaseClientKeys threw (see
      // isPreviewSupabaseKeysTypeError). Same root cause as BAL-169 but
      // surfaces as a different Sentry issue (BAL-171 / BALLEE-BV).
      if (
        resolvedEnvironment !== 'production' &&
        isPreviewSupabaseKeysTypeError(event, eventExceptionValue)
      ) {
        return dropEvent({
          reason: DROP_REASONS.supabaseKeysTypeError,
          errorValue: dropText(),
        });
      }

      // Check for expected validation errors marked by code
      if (
        originalError &&
        typeof originalError === 'object' &&
        'isExpectedValidation' in originalError &&
        (originalError as { isExpectedValidation?: boolean })
          .isExpectedValidation
      ) {
        return dropEvent({
          reason: DROP_REASONS.expectedValidation,
          errorValue: dropText(),
        });
      }

      // Drop errors already captured at the service boundary. Mirror of the
      // server-side filter; see sentry.server.config.ts for the why.
      if (
        originalError &&
        typeof originalError === 'object' &&
        'alreadyCapturedToSentry' in originalError &&
        (originalError as { alreadyCapturedToSentry?: boolean })
          .alreadyCapturedToSentry
      ) {
        return dropEvent({
          reason: DROP_REASONS.alreadyCaptured,
          errorValue: dropText(),
        });
      }

      // Filter ValidationError by name
      if (
        originalError instanceof Error &&
        originalError.name === 'ValidationError'
      ) {
        return dropEvent({
          reason: DROP_REASONS.validationErrorName,
          errorValue: dropText(),
        });
      }

      // Filter GoTrue's AuthWeakPasswordError by name. This is an EXPECTED
      // user-input validation outcome on /auth/sign-up (the user typed a
      // password below the policy minimum) — never a defect. The sign-up UI
      // surfaces it via AuthErrorAlert; it only reaches Sentry because the
      // sign-up flow re-throws it, producing an unhandled rejection. Matching
      // by name (auth-js sets it explicitly, so minification-safe) suppresses
      // it regardless of the length threshold or message wording.
      // See BALLEE-F7 / BALLEE-F9 / BAL-576.
      if (
        originalError instanceof Error &&
        originalError.name === 'AuthWeakPasswordError'
      ) {
        return dropEvent({
          reason: DROP_REASONS.weakPasswordErrorName,
          errorValue: dropText(),
        });
      }

      // Drop the session recorder's own inline-<style> serializer warning
      // (BALLEE-HG). `pattern` carries the regex this branch matched on, which
      // is its own rather than an IGNORED_ERROR_PATTERNS entry — see that
      // guard's docblock for why no such entry can reach this event shape.
      //
      // This is the one drop site that does not call dropText(). The two are
      // indistinguishable here — the guard (third-party-noise-guards.ts) requires event.message to be
      // a non-empty string, and resolveEventText returns exactly that first —
      // so this spelling is kept because it is dev's, not because it differs.
      // Don't "fix" it for uniformity; the equivalence is the whole point.
      if (isRrwebCssSerializerWarning(event)) {
        return dropEvent({
          reason: DROP_REASONS.rrwebCssWarning,
          pattern: RRWEB_CSS_SERIALIZER_WARNING.source,
          errorValue: String(event.message ?? ''),
        });
      }

      // Discard Google Places' own "this place id has changed" notice
      // (BALLEE-JE / BALLEE-JH). Google emits it from its runtime-loaded Maps
      // JS when the id it returned from AutocompletePlaces resolves to a
      // different canonical id at GetPlace. Because the sentence embeds both
      // ids, every distinct address a user types minted a separate Sentry
      // group — see the guard's docblock for why an IGNORED_ERROR_PATTERNS
      // entry cannot reach this event shape, and for the evidence that our
      // stored ids are not the thing going stale.
      //
      // `errorValue` is a stand-in, NOT the real text: a place id base64url-
      // decodes to the address the user typed, and no redaction rule matches
      // it.
      if (isGooglePlacesIdChangedNotice(event)) {
        return dropEvent({
          reason: DROP_REASONS.googlePlacesIdChanged,
          pattern: GOOGLE_PLACES_ID_CHANGED_NOTICE.source,
          errorValue: GOOGLE_PLACES_ID_CHANGED_DROP_VALUE,
        });
      }

      // An injected page harness wraps `addEventListener`, throws inside its
      // own hook, and reaches us three times over for one defect
      // (BALLEE-HQ/HR/HS). Keyed on the harness marker AND the one message it
      // is known to throw — see the guard's docblock for the evidence that the
      // throw site is theirs, and for why neither an IGNORED_ERROR_PATTERNS
      // entry nor `isExternalScriptError` can express this.
      //
      // Placed after the cloudflare-origin-timeout KEEP above, so an outage
      // body arriving during a crawl is still reported.
      if (isPageHarnessAddEventListenerHook(event)) {
        return dropEvent({
          reason: DROP_REASONS.pageHarnessAddEventListenerHook,
          pattern: PAGE_HARNESS_PATTERN_SLUG,
          errorValue: dropText(),
        });
      }

      // Drop Next.js's own RSC-prefetch fallback log (BALLEE-HF / BAL-1006).
      // Checked separately from IGNORED_ERROR_PATTERNS because the identifying
      // text lives in `extra.arguments`, not in either string that list is
      // tested against — see RSC_PREFETCH_FALLBACK_MESSAGE for the full why.
      // Reported through the same BAL-856 telemetry path so the class stays
      // countable in PostHog rather than vanishing silently.
      if (isRscPrefetchFallbackConsoleCapture(event)) {
        return dropEvent({
          reason: DROP_REASONS.rscPrefetchFallback,
          pattern: RSC_PREFETCH_FALLBACK_MESSAGE.source,
          errorValue: RSC_PREFETCH_FALLBACK_DROP_VALUE,
        });
      }

      // A RECOVERED console message is matched against the narrow subset only
      // (BAL-960). The full list holds bare substrings like `/abort/i` that
      // match our own prose log lines by coincidence; see
      // `findRecoveredConsoleIgnoredPattern` for which patterns are safe.
      if (recoveredConsoleMessage) {
        const matched = findRecoveredConsoleIgnoredPattern(
          recoveredConsoleMessage,
        );
        if (matched) {
          return dropEvent({
            reason: DROP_REASONS.ignorePattern,
            pattern: matched,
            errorValue: recoveredConsoleMessage,
          });
        }
      }

      // Filter out transient/ignored errors by message.
      //
      // For a console capture `hint.originalException` is the SAME
      // String()-cast text as the original `event.message` (`[object
      // Object]`), which matches no pattern by construction. So this path is a
      // no-op for that class, exactly as it was before recovery existed.
      const errorMessage =
        originalError instanceof Error
          ? originalError.message
          : typeof originalError === 'string'
            ? originalError
            : '';

      if (errorMessage) {
        const matched = findIgnoredPattern(errorMessage);
        if (matched) {
          return dropEvent({
            reason: DROP_REASONS.ignorePattern,
            pattern: matched,
            errorValue: errorMessage,
          });
        }
      }

      // Also check event exception values
      if (eventExceptionValue) {
        const matched = findIgnoredPattern(eventExceptionValue);
        if (matched) {
          return dropEvent({
            reason: DROP_REASONS.ignorePattern,
            pattern: matched,
            // Report the ORIGINAL-case value, not the lowercased copy used for
            // matching. The server half fixed this under BAL-891 and the
            // browser was left behind: lowercasing fragments grouping in
            // PostHog against the other call site, and it silently defeats the
            // one case-sensitive redaction rule — `/(AKIA|ASIA)[A-Z0-9]{12,}/`
            // cannot match a lowercased AWS key id, so it would ship intact.
            errorValue:
              event.exception?.values?.[0]?.value ?? eventExceptionValue,
          });
        }
      }

      // Filter out errors from third-party scripts (browser extensions, widgets)
      if (isExternalScriptError(event)) {
        return dropEvent({
          reason: DROP_REASONS.externalScript,
          errorValue: dropText(),
        });
      }

      // Chain caller's beforeSend if provided
      if (callerBeforeSend) {
        return callerBeforeSend(event, hint);
      }

      return event;
    },
  });

  // BAL-959: attribute drops made by an EVENT PROCESSOR, which happen strictly
  // before `beforeSend` runs and are therefore invisible to every filter and
  // hook above. Installed after `init` because the SDK registers each
  // integration's `processEvent` as a client event processor during `init`
  // itself, so the list to wrap does not exist until this point.
  //
  // Additive only: each wrapper delegates to the original processor and
  // returns its result untouched. Nothing about what Sentry captures, drops or
  // dedupes changes — which matters, because changing capture behaviour on the
  // strength of an unconfirmed hypothesis would destroy the signal this exists
  // to record.
  if (onProcessorDrop) {
    instrumentEventProcessorDrops({
      client,
      environment: resolvedEnvironment,
      onProcessorDrop,
      onSinkArmed,
      // The client's processor list is only HALF the chain. `prepareEvent`
      // appends the merged global + isolation + current scope processors after
      // it, and a falsy return from any of them is billed `event_processor`
      // identically. @sentry/nextjs's own `filterNextRedirectError` — which
      // drops every `NEXT_REDIRECT` — is registered by `core.addEventProcessor`
      // onto the ISOLATION scope from inside the `init` call above, so it is
      // already present here and would have been invisible to a client-only
      // wrap. Passed as resolvers, not scopes: the current scope is a fresh
      // clone inside every `withScope`.
      scopes: [
        { kind: 'global', resolve: getGlobalScope },
        { kind: 'isolation', resolve: getIsolationScope },
        { kind: 'current', resolve: getCurrentScope },
      ],
    });
  }

  return client;
}
